import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDate } from '../lib/utils';
import { latestOnly } from '../lib/latestOnly';
import { parseWashesInput, washesCeiling, errorDetail } from '../lib/washesInput';
import UserCombobox from '../components/UserCombobox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, CreditCard, Search, X, Clock, Car, Minus, Pencil, Gift, Ban } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The typed wash count. Rendered inside the row keyed by subscription id, so a poll that re-sorts or
// filters the list never hands one row's draft to another. The draft exists only while
// focused and is committed only if the admin actually typed — a click in and out must never
// send the number that was on screen, which may be seconds stale.
function WashesInput({ saved, ceiling, disabled, onCommit }) {
  const [draft, setDraft] = useState(String(saved));
  const [focused, setFocused] = useState(false);
  const shownRef = useRef(saved);
  const dirtyRef = useRef(false);
  const skipRef = useRef(false);

  const onFocus = () => {
    shownRef.current = saved;
    dirtyRef.current = false;
    skipRef.current = false;
    setDraft(String(saved));
    setFocused(true);
  };

  const onBlur = () => {
    setFocused(false);
    if (skipRef.current || !dirtyRef.current) return;
    const parsed = parseWashesInput(draft, ceiling);
    if (!parsed.ok) {
      toast.error(parsed.error);
      return;
    }
    if (parsed.value !== shownRef.current) onCommit(parsed.value, shownRef.current);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      skipRef.current = true; // read by onBlur, which runs before React re-renders
      e.currentTarget.blur();
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label="Spălări rămase"
      value={focused ? draft : String(saved)}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onChange={(e) => { dirtyRef.current = true; setDraft(e.target.value); }}
      className="w-12 h-6 rounded border border-border bg-transparent text-center font-medium tabular-nums focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
    />
  );
}

const Subscriptions = () => {
  const { t, language } = useLanguage();
  const { isViewer, isAdmin } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updatingWashes, setUpdatingWashes] = useState(null);
  const [endDateDialogOpen, setEndDateDialogOpen] = useState(false);
  const [endDateSub, setEndDateSub] = useState(null);
  const [endDateValue, setEndDateValue] = useState('');
  const [savingEndDate, setSavingEndDate] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokeSub, setRevokeSub] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCars, setUserCars] = useState([]);
  const [loadingCars, setLoadingCars] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    subscription_type_id: '',
    car_id: ''
  });

  // Only the newest user selection's cars may reach the form. Held in a ref so
  // the gate survives re-renders: a gate rebuilt each render would hand every
  // request ticket 1 and never detect a superseded response. (useRef evaluates
  // its argument on every render but keeps only the first result, so the gate
  // is created once despite how this line reads.)
  const carsRequest = useRef(latestOnly());

  // Revoking a gift is admin-only on the backend (get_admin_user), so a viewer must not
  // see the button at all — a 403 they cannot avoid is not an interface. `!isViewer`
  // mirrors how every other mutating control on this page is gated; the two flags are
  // independent in the database, so both are checked.
  const canRevoke = isAdmin && !isViewer;

  useEffect(() => {
    fetchData();
    // Refresh silent periodic doar pe abonamente, ca spălările rămase
    // să scadă vizual când kioskul le contorizează (fără reload manual).
    const interval = setInterval(refreshSubscriptions, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, usersRes, typesRes] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/subscription-types`)
      ]);
      setSubscriptions(subsRes.data);
      setUsers(usersRes.data);
      setSubscriptionTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  // Bumped on every washes write. The 5 s poll captures it before its GET and drops the
  // response if a write happened meanwhile — otherwise a poll that started just before a save
  // lands after it and shows the old number, and the admin's retype then gets a 409.
  const washesWriteSeq = useRef(0);

  const refreshSubscriptions = async () => {
    const seq = washesWriteSeq.current;
    try {
      const subsRes = await axios.get(`${API}/admin/subscriptions`);
      if (seq !== washesWriteSeq.current) return; // a washes write landed meanwhile; next poll
      setSubscriptions(subsRes.data);
    } catch (error) {
      // poll silent — ignorăm erorile, păstrăm datele curente
    }
  };

  const handleUserSelect = async (userId) => {
    // Functional update as a matter of habit, not as a fix: two dropdown clicks
    // are separate browser events with a React flush between them, so the
    // literal spread this replaced could not actually have lost an update. It
    // costs nothing and stays correct if this is ever called twice in one tick.
    setFormData(prev => ({ ...prev, user_id: userId, car_id: '' }));
    setUserCars([]);

    const user = users.find(u => u.id === userId);
    if (!user) return;

    setSelectedUser(user);
    setLoadingCars(true);

    const result = await carsRequest.current(() =>
      axios.get(`${API}/admin/users/${userId}/cars`)
    );

    // A newer selection is in flight and owns this state now — including the
    // spinner, which must keep running for that request rather than be switched
    // off by this superseded one.
    if (result.stale) return;

    setLoadingCars(false);

    // `!result.ok`, not `result.error`: a task can reject with no reason at
    // all, and a truthiness check would read that failure as a success and then
    // dereference a `value` that was never set.
    if (!result.ok) {
      console.error('Error fetching user cars:', result.error);
      setUserCars([]);
      return;
    }

    setUserCars(result.value.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.user_id || !formData.subscription_type_id) {
      toast.error(language === 'ro' ? 'Selectează utilizator și tip abonament' : 'Select user and subscription type');
      return;
    }

    if (!formData.car_id) {
      toast.error(language === 'ro' ? 'Selectează o mașină' : 'Select a car');
      return;
    }

    try {
      await axios.post(`${API}/admin/subscriptions?user_id=${formData.user_id}`, {
        subscription_type_id: formData.subscription_type_id,
        car_id: formData.car_id
      });
      toast.success(t('subscription_created'));
      setDialogOpen(false);
      setFormData({ user_id: '', subscription_type_id: '', car_id: '' });
      setSelectedUser(null);
      setUserCars([]);
      fetchData();
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.response?.data?.detail || 'Error creating subscription');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setFormData({ user_id: '', subscription_type_id: '', car_id: '' });
      setSelectedUser(null);
      setUserCars([]);
    }
  };

  const getStatusBadge = (subscription) => {
    const now = new Date();
    const endDate = new Date(subscription.subscription.end_date);

    // Checked before the date: revoking zeroes the washes but leaves end_date alone, so
    // a gift clawed back today keeps a future end_date and the two checks below would
    // label it "Activ" — the exact opposite of what happened to it.
    if (subscription.subscription.status === 'revoked') {
      return <Badge variant="destructive">Revocat</Badge>;
    }
    if (subscription.subscription.status === 'expired' || endDate < now) {
      return <Badge variant="destructive">{t('expired')}</Badge>;
    }
    return <Badge variant="default">{t('active')}</Badge>;
  };

  const applyWashResult = (subId, data) => {
    setSubscriptions(prev => prev.map(s =>
      s.subscription.id === subId
        ? { ...s, subscription: { ...s.subscription, washes_remaining: data.washes_remaining, status: data.status ?? s.subscription.status } }
        : s
    ));
  };

  const updateWashes = async (subId, params) => {
    washesWriteSeq.current += 1;
    setUpdatingWashes(subId);
    try {
      const res = await axios.patch(`${API}/admin/subscriptions/${subId}/washes`, null, { params });
      applyWashResult(subId, res.data);
    } catch (error) {
      toast.error(errorDetail(error, 'Eroare la actualizarea spălărilor'));
      refreshSubscriptions();
    } finally {
      setUpdatingWashes(null);
    }
  };

  const handleWashDelta = (subId, delta) => updateWashes(subId, { delta });
  const handleWashValue = (subId, value, expected) => {
    // Show the typed number right away instead of flashing the old one during the request; a
    // failure re-reads the list (updateWashes' catch), which puts the real value back.
    applyWashResult(subId, { washes_remaining: value });
    return updateWashes(subId, { value, expected });
  };

  const openEndDateDialog = (sub) => {
    setEndDateSub(sub);
    // Use local Y/M/D (not toISOString(), which is UTC) so the pre-filled value
    // matches what the table row displays via formatDate() — otherwise
    // this can silently pre-fill the wrong calendar day whenever the stored
    // end_date's time-of-day crosses a UTC offset boundary (e.g. Romania,
    // UTC+2/+3, for any end_date before ~02:00-03:00 local time).
    const d = new Date(sub.subscription.end_date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    setEndDateValue(`${yyyy}-${mm}-${dd}`);
    setEndDateDialogOpen(true);
  };

  const handleSaveEndDate = async (e) => {
    e.preventDefault();
    if (!endDateValue) return;
    setSavingEndDate(true);
    try {
      const res = await axios.patch(
        `${API}/admin/subscriptions/${endDateSub.subscription.id}/end-date`,
        { end_date: new Date(endDateValue).toISOString() }
      );
      setSubscriptions(prev => prev.map(s =>
        s.subscription.id === endDateSub.subscription.id
          ? { ...s, subscription: { ...s.subscription, end_date: res.data.end_date, status: res.data.status } }
          : s
      ));
      toast.success('Data de expirare a fost actualizată');
      setEndDateDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea datei');
    } finally {
      setSavingEndDate(false);
    }
  };

  const openRevokeDialog = (sub) => {
    setRevokeSub(sub);
    setRevokeDialogOpen(true);
  };

  const handleRevokeGift = async () => {
    if (!revokeSub) return;
    setRevoking(true);
    try {
      const res = await axios.post(`${API}/admin/referrals/gift/${revokeSub.subscription.id}/revoke`);
      const reclaimed = res.data.washes_reclaimed;
      const granted = res.data.washes_granted;
      toast.success(granted > 0
        ? `Cadou revocat — ${reclaimed} din ${granted} spălări au fost retrase`
        : `Cadou revocat — ${reclaimed} spălări au fost retrase`);
      setRevokeDialogOpen(false);
      setRevokeSub(null);
      // The revoke response carries no revoked_at/washes_revoked, and the row has to show
      // both — so re-read the list instead of patching it locally.
      await refreshSubscriptions();
    } catch (error) {
      // The endpoint answers 400 with a Romanian sentence saying exactly why (already
      // revoked, already expired, nothing left to take back). That is the message the
      // operator needs, not "Eroare".
      toast.error(error.response?.data?.detail || 'Eroare la revocarea cadoului');
      // A 400 here means the row on screen is out of date (someone else revoked it, it
      // lapsed, the customer spent the last wash). Re-read so the table stops showing a
      // Revocă button for a gift that no longer has anything to revoke.
      await refreshSubscriptions();
    } finally {
      setRevoking(false);
    }
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = 
      sub.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.license_plate.toLowerCase().includes(searchQuery.toLowerCase());
    
    const now = new Date();
    const endDate = new Date(sub.subscription.end_date);
    const isActive = sub.subscription.status === 'active' && endDate > now;
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && isActive) ||
      (filterStatus === 'expired' && !isActive);
    
    const matchesType = filterType === 'all' || sub.subscription.subscription_type_id === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="subscriptions-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{t('subscriptions')}</h1>
          <p className="text-muted-foreground mt-1">
            {subscriptions.length} {t('total_subscriptions').toLowerCase()}
          </p>
        </div>
        {!isViewer && (
          <Button onClick={() => setDialogOpen(true)} data-testid="add-subscription-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('add_subscription')}
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-subscriptions"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-status">
                <SelectValue placeholder={t('filter_by_status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="active">{t('active')}</SelectItem>
                <SelectItem value="expired">{t('expired')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-type">
                <SelectValue placeholder={t('filter_by_type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {subscriptionTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(filterStatus !== 'all' || filterType !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
                <X className="w-4 h-4 mr-1" />
                {t('clear_filters')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredSubscriptions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('user_name')}</TableHead>
                  <TableHead>{t('phone')}</TableHead>
                  <TableHead>{t('license_plate')}</TableHead>
                  <TableHead>{t('subscription_types')}</TableHead>
                  <TableHead>{t('start_date')}</TableHead>
                  <TableHead>{t('end_date')}</TableHead>
                  <TableHead>{t('washes_remaining')}</TableHead>
                  <TableHead>{t('status')}</TableHead>
                  <TableHead>Recomandat de</TableHead>
                  {canRevoke && <TableHead className="text-right">{t('actions')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscriptions.map((sub) => {
                  const isActive = sub.subscription.status === 'active' && new Date(sub.subscription.end_date) > new Date();
                  // Read off the flag the backend sets, never guessed from the type name:
                  // a gift is not something the customer bought, has no car, and is the
                  // only row on this page that can be revoked.
                  const isGift = sub.subscription.is_gift_reward === true;
                  const isRevoked = sub.subscription.status === 'revoked';
                  // Mirrors admin_update_washes: 100, never below a gift's grant or the
                  // current value (the ceiling limits increases only).
                  const washCeiling = washesCeiling(sub.subscription.washes_granted, sub.subscription.washes_remaining);
                  return (
                  <TableRow
                    key={sub.subscription.id}
                    className={isGift ? 'bg-primary/5' : undefined}
                    data-testid={`subscription-row-${sub.subscription.id}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {isGift
                          ? <Gift className="w-4 h-4 text-primary" />
                          : <CreditCard className="w-4 h-4 text-primary" />}
                        {sub.user_name}
                      </div>
                    </TableCell>
                    <TableCell>{sub.user_phone}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-muted-foreground" />
                        {/* A gift has no car_id, so the listing sends "N/A" here. It is
                            not a missing plate: the gift is spendable on any car the
                            owner has registered. */}
                        {isGift
                          ? <span className="text-muted-foreground italic">orice mașină</span>
                          : sub.license_plate}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {isGift ? (
                          <Badge variant="default" className="gap-1">
                            <Gift className="w-3 h-3" />
                            {sub.subscription_type_name}
                          </Badge>
                        ) : (
                          <Badge variant="outline">{sub.subscription_type_name}</Badge>
                        )}
                        {/* A v2 referral reward row: an ordinary category subscription
                            (its plate/category shown as for paid rows), never bought. */}
                        {sub.subscription.is_referral_reward === true && (
                          <Badge
                            variant="secondary"
                            className="gap-1"
                            data-testid={`referral-reward-badge-${sub.subscription.id}`}
                          >
                            <Gift className="w-3 h-3" />
                            {language === 'ro' ? 'Din recomandare' : 'Referral reward'}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-3 h-3" />
                        {formatDate(sub.subscription.start_date)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-3 h-3" />
                        {formatDate(sub.subscription.end_date)}
                        {/* Never on a revoked gift: a revocation is a decision about
                            the row, not a date, and this dialog is not the place to
                            undo one. The backend keeps the status either way. */}
                        {!isViewer && !isRevoked && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEndDateDialog(sub)}
                            title="Editează data de expirare"
                            data-testid={`edit-end-date-${sub.subscription.id}`}
                          >
                            <Pencil className="w-3 h-3 text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {sub.subscription.washes_remaining !== null ? (
                        isActive && !isViewer ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleWashDelta(sub.subscription.id, -1)}
                              disabled={updatingWashes === sub.subscription.id || sub.subscription.washes_remaining <= 0}
                              className="w-6 h-6 rounded flex items-center justify-center border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <WashesInput
                              saved={sub.subscription.washes_remaining}
                              ceiling={washCeiling}
                              disabled={updatingWashes === sub.subscription.id}
                              onCommit={(value, expected) => handleWashValue(sub.subscription.id, value, expected)}
                            />
                            <button
                              onClick={() => handleWashDelta(sub.subscription.id, 1)}
                              disabled={updatingWashes === sub.subscription.id || sub.subscription.washes_remaining >= washCeiling}
                              className="w-6 h-6 rounded flex items-center justify-center border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <span className={isActive ? 'font-medium' : 'text-muted-foreground'}>
                            {sub.subscription.washes_remaining}
                          </span>
                        )
                      ) : (
                        t('unlimited')
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(sub)}
                      {isRevoked && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {sub.subscription.washes_revoked ?? 0} spălări retrase
                          {sub.subscription.revoked_at && ` · ${formatDate(sub.subscription.revoked_at)}`}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>{sub.operator_name || '—'}</TableCell>
                    {canRevoke && (
                      <TableCell className="text-right">
                        {isGift && !isRevoked && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRevokeDialog(sub)}
                            title="Revocă abonamentul cadou"
                            data-testid={`revoke-gift-${sub.subscription.id}`}
                          >
                            <Ban className="w-4 h-4 text-destructive mr-1" />
                            Revocă
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <CreditCard className="w-12 h-12 mb-4 opacity-50" />
              <p>{t('no_subscriptions')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[500px]" data-testid="subscription-dialog">
          <DialogHeader>
            <DialogTitle>{t('add_subscription')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Select User */}
              <div className="space-y-2">
                <Label>{t('select_user')} *</Label>
                <UserCombobox
                  users={users}
                  value={formData.user_id}
                  onChange={handleUserSelect}
                  placeholder={t('select_user')}
                />
              </div>

              {/* Select Car - Only visible after user is selected */}
              {formData.user_id && (
                <div className="space-y-2">
                  <Label>{t('select_car')} *</Label>
                  {loadingCars ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      <span className="ml-2 text-sm text-muted-foreground">
                        {language === 'ro' ? 'Se încarcă mașinile...' : 'Loading cars...'}
                      </span>
                    </div>
                  ) : userCars.length > 0 ? (
                    <Select
                      value={formData.car_id}
                      onValueChange={(value) => setFormData({ ...formData, car_id: value })}
                    >
                      <SelectTrigger data-testid="select-car">
                        <SelectValue placeholder={t('select_car')} />
                      </SelectTrigger>
                      <SelectContent>
                        {userCars.map(car => (
                          <SelectItem key={car.id} value={car.id}>
                            <div className="flex items-center gap-2">
                              <Car className="w-4 h-4" />
                              <span className="font-medium">{car.license_plate}</span>
                              {(car.brand || car.model) && (
                                <span className="text-muted-foreground">
                                  - {car.brand} {car.model}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-sm text-muted-foreground p-3 border rounded-md bg-muted/50">
                      <Car className="w-4 h-4 inline mr-2" />
                      {language === 'ro' 
                        ? 'Acest utilizator nu are mașini înregistrate.' 
                        : 'This user has no registered cars.'}
                    </div>
                  )}
                </div>
              )}
              
              {/* Select Subscription Type */}
              <div className="space-y-2">
                <Label>{t('select_subscription_type')} *</Label>
                <Select
                  value={formData.subscription_type_id}
                  onValueChange={(value) => setFormData({ ...formData, subscription_type_id: value })}
                >
                  <SelectTrigger data-testid="select-subscription-type">
                    <SelectValue placeholder={t('select_subscription_type')} />
                  </SelectTrigger>
                  <SelectContent>
                    {subscriptionTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} - {type.price} {t('ron')} ({type.duration_days} {t('days')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>
                {t('cancel')}
              </Button>
              <Button 
                type="submit" 
                data-testid="save-subscription-btn"
                disabled={!formData.user_id || !formData.car_id || !formData.subscription_type_id}
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Revoke Gift Dialog */}
      <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="revoke-gift-dialog">
          <DialogHeader>
            <DialogTitle>
              Revoci abonamentul cadou — {revokeSub?.user_name}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-muted-foreground">
            <p>
              Spălările rămase{' '}
              <span className="font-medium text-foreground">
                ({revokeSub?.subscription?.washes_remaining ?? 'nelimitat'})
              </span>{' '}
              sunt aduse la 0 și nu mai pot fi folosite la kiosk.
            </p>
            <p>
              Spălările deja consumate <span className="font-medium text-foreground">nu</span> se
              retrag, iar recomandarea rămâne înregistrată în statistici.
            </p>
            <p>Acțiunea nu poate fi anulată.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRevokeDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleRevokeGift}
              disabled={revoking}
              data-testid="confirm-revoke-gift-btn"
            >
              {revoking ? 'Se revocă...' : 'Revocă'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit End Date Dialog */}
      <Dialog open={endDateDialogOpen} onOpenChange={setEndDateDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="edit-end-date-dialog">
          <DialogHeader>
            <DialogTitle>
              Editează data de expirare — {endDateSub?.user_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEndDate}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-end-date">Data de expirare</Label>
                <Input
                  id="edit-end-date"
                  type="date"
                  value={endDateValue}
                  onChange={(e) => setEndDateValue(e.target.value)}
                  data-testid="edit-end-date-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEndDateDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={savingEndDate} data-testid="save-end-date-btn">
                {savingEndDate ? 'Se salvează...' : t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;
